/*
 * The Atomz chat tile that sits inside the product grid.
 *
 * WHY THIS IS THEME CODE AND NOT AN APP BLOCK
 *
 * The app ships three chat surfaces: az-chat-pdp (a product page), and
 * az-chat-launcher (a floating button), both thin wrappers over the shared SDK
 * in az-chat-sdk.js. There is no block for a card sitting inside a collection
 * grid, so this is that wrapper, written the same way theirs are: it does not
 * reimplement any chat, it initialises the same SDK and calls the same
 * AtomzChat.open().
 *
 * WHERE THE WORDS COME FROM
 *
 * AtomzChat.widgetConfig() returns what the merchant set in the app admin -
 * welcomeTitle, welcomeBody, and the starter chips. That wins over anything set
 * in the theme editor, which is the precedence the app's own launcher uses
 * (app -> theme -> default). The tile renders nothing until it has real text,
 * so a store that has not configured the widget gets no tile rather than a
 * placeholder greeting.
 *
 * WHERE THE QUESTIONS COME FROM
 *
 * The same place the "Narrow it down" row above the grid does. Every page the
 * app is on carries a #page-data script -- pageType, the collection's id,
 * title, description and tags -- and query-discovery-propmts answers it with
 * prompts written for that collection, pipe-separated, and the number of them
 * to show. On Sandals that is "Flat Leather Sandals", "Block Heel Sandals",
 * "Wide Fit Sandals"; the theme could not have written those, and a theme-set
 * list would be the same three questions on every collection in the shop.
 *
 * The theme's own chips stay as the fallback and stay on screen while the
 * request is in flight, so the tile never flashes empty and a shop the
 * service does not answer for keeps working.
 *
 * LOADING THE SDK
 *
 * Derived from whatever Atomz script is already on the page rather than
 * hard-coded, because the extension URL carries a version that changes every
 * time the app is released. The search bar block is in the header group on
 * every page, so there is always one to read.
 */
(function () {
  'use strict';

  var SDK = 'az-chat-sdk.js';
  var CSS = 'az-chat.css';
  var SERVICE = 'https://app.getatomz.com';
  var PROMPTS = '/search-service/query-discovery-propmts';

  function extensionBase() {
    var tags = document.querySelectorAll('script[src*="atomz-ai-search-prompts"]');
    for (var i = 0; i < tags.length; i++) {
      var src = tags[i].getAttribute('src') || '';
      var cut = src.lastIndexOf('/');
      if (cut > 0) return src.slice(0, cut + 1);
    }
    return null;
  }

  function loadOnce(url, isCss) {
    var sel = isCss ? 'link[href="' + url + '"]' : 'script[src="' + url + '"]';
    if (document.querySelector(sel)) return;
    var el;
    if (isCss) {
      el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = url;
    } else {
      el = document.createElement('script');
      el.src = url;
      el.defer = true;
    }
    document.head.appendChild(el);
  }

  function ensureSdk() {
    return new Promise(function (resolve, reject) {
      if (window.AtomzChat) return resolve(window.AtomzChat);

      var base = extensionBase();
      if (!base) return reject(new Error('no atomz script on the page'));

      loadOnce(base + CSS, true);
      loadOnce(base + SDK, false);

      // The SDK assigns window.AtomzChat at the end of its own evaluation and
      // fires no event, so poll briefly rather than race it.
      var waited = 0;
      var tick = setInterval(function () {
        if (window.AtomzChat) {
          clearInterval(tick);
          resolve(window.AtomzChat);
        } else if ((waited += 100) > 8000) {
          clearInterval(tick);
          reject(new Error('AtomzChat did not arrive'));
        }
      }, 100);
    });
  }

  function text(el, value) {
    if (el && value) el.textContent = value;
  }

  /* The service's own origin where the header names one, so a staging endpoint
     in the theme editor moves both calls rather than just the search. */
  function serviceBase() {
    try {
      var configured = window.ATOMZ_SEARCH_ENDPOINT;
      if (configured) return new URL(configured, window.location.href).origin;
    } catch (e) {}
    return SERVICE;
  }

  function shopDomain() {
    return (window.Shopify && window.Shopify.shop) || window.ATOMZ_SHOP || '';
  }

  function pageData() {
    var el = document.getElementById('page-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* n of them, picked at random, which is what the app's own prompt block does
     with the same response: the service returns twenty and says how many to
     show, so two shoppers on one collection do not see an identical row. */
  function sample(list, n) {
    var pool = list.slice();
    var out = [];
    while (pool.length && out.length < n) {
      out.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
    }
    return out;
  }

  function chips(tile, list) {
    var host = tile.querySelector('.az-chat-tile__chips');
    if (!list.length) return;

    if (!host) {
      host = document.createElement('ul');
      host.className = 'az-chat-tile__chips';
      var before = tile.querySelector('[data-az-chat-input]');
      if (before) tile.insertBefore(host, before);
      else tile.appendChild(host);
    }

    host.innerHTML = '';
    list.forEach(function (q) {
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'az-chat-tile__chip';
      b.setAttribute('data-atomz-prompt', q);
      b.textContent = q;
      li.appendChild(b);
      host.appendChild(li);
    });
  }

  /*
   * Replaces the theme's chips only once real ones arrive. Any failure -- no
   * page data, no answer, an answer with nothing in it -- leaves what was
   * already on screen, because the fallback questions still open the assistant
   * and an empty tile does not.
   */
  function askForPrompts(tile) {
    var page = pageData();
    var shop = shopDomain();
    if (!page || !shop || !window.fetch) return;

    fetch(serviceBase() + PROMPTS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page_data: page, shop: shop }),
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || d.error || typeof d.value !== 'string') return;
        var all = d.value.split('|')
          .map(function (q) { return q.trim(); })
          .filter(Boolean);
        if (!all.length) return;
        chips(tile, sample(all, d.numberOfResults || 3));
      })
      .catch(function () { /* the theme's own questions stand */ });
  }

  function wire(tile, chat) {
    // Every clickable thing in the tile opens the panel. A chip sends its own
    // question; the input and the card open it empty, because the panel has a
    // real input and duplicating one here would only lose what was typed.
    tile.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-atomz-prompt]');
      e.preventDefault();
      chat.open(chip ? chip.getAttribute('data-atomz-prompt') : undefined);
    });

    var input = tile.querySelector('[data-az-chat-input]');
    if (input) {
      // Never let it take a keystroke it cannot keep.
      input.addEventListener('focus', function () { input.blur(); chat.open(); });
      input.addEventListener('pointerdown', function (e) { e.preventDefault(); chat.open(); });
    }
  }

  function dress(tile, chat) {
    // Theme text is the fallback; the app admin wins, same order the app's own
    // launcher uses.
    if (typeof chat.widgetConfig !== 'function') return Promise.resolve();
    return chat.widgetConfig().then(function (cfg) {
      cfg = cfg || {};
      text(tile.querySelector('[data-az-chat-title]'), cfg.welcomeTitle);
      text(tile.querySelector('[data-az-chat-body]'), cfg.welcomeBody);
    }).catch(function () { /* the theme's own text stands */ });
  }

  function start() {
    var tile = document.querySelector('[data-az-chat-tile]');
    if (!tile) return;

    ensureSdk().then(function (chat) {
      chat.init({
        pageType: tile.getAttribute('data-page-type') || 'collection',
        locale: tile.getAttribute('data-locale') || null,
        customerId: tile.getAttribute('data-customer-id') || null,
        customerName: tile.getAttribute('data-customer-name') || null,
      });
      wire(tile, chat);
      tile.removeAttribute('data-az-chat-pending');
      askForPrompts(tile);
      return dress(tile, chat);
    }).catch(function () {
      // No SDK, no chat, no tile. The grid closes the gap on its own.
      if (tile.parentNode) tile.parentNode.removeChild(tile);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
